import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { OnoffType, PropSeriesType, QualityType } from '@/api/enum/common.enum';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { ExerciseSeriesPinnedFilter } from '@/api/DTO/exercise/dingDay21.interface';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useGetRegularRewardListQuery } from '@/api/task/regularApiSlice';
import { TaskScheduledRewardDTO } from '@/api/DTO/task/regular.interface';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import { useGetRewardListQuery } from '@/api/common';
import { GameRewardDetailDTO } from '@/api/DTO/common.interface';

interface Props {
	handleGetRewardList: (list: any[]) => void;
}

export default function AddRewardItemDialog(props: Props) {
	const { handleGetRewardList } = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState<any[]>([]);

	useEffect(() => {
		setSelectedData([]);
	}, [open]);

	const propSeriesTypeList = [
		{
			id: PropSeriesType.Card,
			name: t('taskManage.card') + t('taskManage.treasure'),
		},
		{
			id: PropSeriesType.Chartlet,
			name: t('taskManage.chartlet') + t('taskManage.treasure'),
		},
		{
			id: PropSeriesType.Gallery,
			name: t('taskManage.gallery') + t('taskManage.treasure'),
		},
		{
			id: PropSeriesType.Puzzle,
			name: t('taskManage.puzzle') + t('taskManage.treasure'),
		},
		{
			id: PropSeriesType.Email,
			name: t('taskManage.email') + t('taskManage.treasure'),
		},
	];

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const defaultPropertyFilter: ExerciseSeriesPinnedFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<ExerciseSeriesPinnedFilter> =
		defaultPropertyFilter;
	const methods = useForm<ExerciseSeriesPinnedFilter>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetRewardListQuery(methods.getValues(), {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	useEffect(() => {}, [methods.watch()]);
	const onSubmit = async () => {
		handleGetRewardList(selectedData);
		handleClose();
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'propType',
			headerName: t('taskManage.propType'),
			flex: 21,
			renderCell: (params: any) =>
				showLabel(params.row.propType, propSeriesTypeList),
		},
		{
			field: 'seriesTitleZh',
			headerName: t('taskManage.propSeries'),
			flex: 21,
		},
		{
			field: 'titleZh',
			headerName: t('taskManage.propName'),
			flex: 21,
		},
		{
			field: 'quality',
			headerName: t('taskManage.propQuality'),
			flex: 21,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
		{
			field: 'published',
			headerName: t('meditation.seriesStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedEndAt',
			headerName: t('box.seriesPulledTime'),
			flex: 21,
		},
	];

	const handlechange = (text: string) => {
		methods.reset({ ...defaultPropertyFilter, searchString: text });
	};

	const handleSelect = (rows: string[]) => {
		console.log(rows);
		if (rows.length > 1) {
			return enqueueSnackbar(t('exerciseSetting.max1'), {
				variant: 'warning',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}

		const obj = propertySummary?.data?.items.find(
			(item) => item.rewardId === rows[0]
		);
		const arr = [
			{
				rewardId: rows[0],
				propType: obj?.propType,
				titleZh: obj?.seriesTitleZh + ' - ' + obj?.titleZh,
			},
		];

		setSelectedData(rows.length > 0 ? arr : []);
	};

	const transformResponse = useCallback((response?: GameRewardDetailDTO[]) => {
		if (!response) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				publishedEndAt: !item.publishedEndAt
					? '-'
					: dayjs(item.publishedEndAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<Button
				onClick={() => setOpen(true)}
				endIcon={<AddCircleOutlineOutlinedIcon />}
			>
				{t('taskManage.addRewardItem')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('taskManage.addRewardItem')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						marginBottom={'10px'}
					>
						<Typography
							variant='body2'
							sx={{
								display: 'flex',
							}}
						>
							<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
							&nbsp;
							{t('taskManage.onlyOne')}
						</Typography>
					</Box>
					<FormProvider {...methods}>
						<AppSummarySearchBar
							name='searchString'
							control={methods.control}
							placeholder={t('common.search')}
							onchange={(text) => {
								handlechange(text);
							}}
						/>
						<div style={{ height: '20px' }}></div>
						<AppDataGrid
							checkboxSelection
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.rewardId}
							onSelectionModelChange={(selectionModel) => {
								handleSelect(selectionModel as string[]);
							}}
							selectionModel={selectedData.map(
								(item: { rewardId: any }) => item.rewardId
							)}
						/>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.add')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
